import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Tag } from 'lucide-react';
import { usePost } from '../hooks/usePosts';
import { formatDate } from '../utils/formatDate';

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { post, loading, error } = usePost(slug);

  if (loading) {
    return (
      <section style={{ paddingTop: '120px', paddingBottom: '4rem', minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="container">
          <p style={{ fontSize: '1.125rem', color: 'var(--text-secondary)' }}>Loading...</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section style={{ paddingTop: '120px', paddingBottom: '4rem', minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="container">
          <div style={{
            background: '#fee8e8',
            border: '1px solid #d96f7b',
            borderRadius: '0.5rem',
            padding: '1.5rem',
            color: '#c42a45'
          }}>
            <p style={{ margin: 0 }}>Post not found or error loading post.</p>
          </div>
          <button
            onClick={() => navigate('/blog')}
            style={{
              marginTop: '1.5rem',
              padding: '0.75rem 1.5rem',
              background: 'var(--teal)',
              color: 'white',
              border: 'none',
              borderRadius: '0.375rem',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            Back to Blog
          </button>
        </div>
      </section>
    );
  }

  if (!post) {
    return (
      <section style={{ paddingTop: '120px', paddingBottom: '4rem', minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="container">
          <p style={{ fontSize: '1.125rem', color: 'var(--text-secondary)' }}>Post not found.</p>
        </div>
      </section>
    );
  }

  return (
    <>
      {/* Back Button and Breadcrumb */}
      <section style={{ paddingTop: '120px', paddingBottom: '2rem', background: 'var(--off-white)' }}>
        <div className="container">
          <button
            onClick={() => navigate('/blog')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: 'var(--teal)',
              fontWeight: '600',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              marginBottom: '1rem'
            }}
          >
            <ArrowLeft size={16} />
            Back to Blog
          </button>
        </div>
      </section>

      {/* Featured Image */}
      {post.featuredImage && (
        <section style={{ paddingBottom: '3rem' }}>
          <div style={{
            width: '100%',
            height: '400px',
            backgroundImage: `url(${post.featuredImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }} />
        </section>
      )}

      {/* Post Content */}
      <section className="section">
        <div className="container" style={{ maxWidth: '800px' }}>
          {/* Title */}
          <h1 style={{ marginBottom: '1.5rem', color: 'var(--navy)' }}>
            {post.title}
          </h1>

          {/* Meta Information */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '2rem',
            alignItems: 'center',
            paddingBottom: '2rem',
            borderBottom: '1px solid var(--border-color)',
            marginBottom: '2rem'
          }}>
            {/* Date */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-secondary)' }}>
              <Calendar size={16} />
              <span>{formatDate(post.publishedAt)}</span>
            </div>

            {/* Author */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-secondary)' }}>
              <User size={16} />
              <span>{post.author?.name || 'ROLF'}</span>
            </div>

            {/* Category */}
            {post.category && (
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: post.category.color,
                color: 'white',
                padding: '0.35rem 0.75rem',
                borderRadius: '0.25rem',
                fontSize: '0.85rem',
                fontWeight: '600'
              }}>
                <Tag size={14} />
                {post.category.name}
              </div>
            )}
          </div>

          {/* Excerpt */}
          <p style={{
            fontSize: '1.25rem',
            color: 'var(--text-secondary)',
            marginBottom: '2rem',
            fontWeight: '500'
          }}>
            {post.excerpt}
          </p>

          {/* Content */}
          <div
            className="blog-content"
            style={{
              fontSize: '1rem',
              lineHeight: '1.8',
              color: 'var(--text-dark)',
              marginBottom: '3rem'
            }}
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* Back to Blog Button */}
          <button
            onClick={() => navigate('/blog')}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'var(--teal)',
              color: 'white',
              border: 'none',
              borderRadius: '0.375rem',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '1rem'
            }}
          >
            Back to Blog
          </button>
        </div>
      </section>
    </>
  );
};

export default BlogPostPage;
